/* eslint-disable object-curly-newline */
import './TimeAccessDuration.css';

import {
  Button,
  Chip,
  DateAndTimePicker,
  InputField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';
import {
  add,
  addMinutes,
  differenceInMinutes,
  format,
  startOfDay,
} from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

/**
 * #### WIP Todo:
 * - Replace Shared max/min date with API criteria
 * - Set Chips disabled if min/max time is not available
 * - Dropdown Now Selection should have Minutes, Hours, Days (Depends on the maximum allowable request time customer’s admin has set. “Days” won’t be an option sometimes)
 * - Refactor getFutureDateAndTime when we figure out how to handle min max times from API
 * - Discuss vocabular of days and hours with design and implement (i.e. we probably want 7days not 168hours)
 * - Update Chips isDisabled when rangeMaxDate is to close
 * - If now and custom duration the continue button does not show up till you select custom again
 */

function TimePicker({
  duration,
  startDate,
  endDate,
  setAccordionValue,
  setDuration,
  setStartDate,
  setEndDate,
}) {
  const [dropdown, setDropdown] = useState(false);
  const [toggleChoice, setToggleChoice] = useState(1);
  const [futureChipDuration, setFutureChipDuration] = useState(false);
  const [durationIsCustom, setDurationIsCustom] = useState(false);
  const [customDuration, setCustomDuration] = useState(null);
  // TODO: Replace Shared max/min date with API criteria
  const rangeMaxDate = new Date('December 16, 2024');
  const rangeMinDate = startOfDay(new Date());
  const now = new Date();

  // Now or Future
  const isNow = toggleChoice === 1;

  // Unset the customDuration if toggle button group is changed
  useEffect(() => {
    // Can not use null like I want to here, it breaks the Button render function
    setCustomDuration(undefined);

    if (isNow && futureChipDuration) {
      setDuration(futureChipDuration);
    }
  }, [toggleChoice]);

  // Custom end date handler
  useEffect(() => {
    if (startDate && endDate) {
      const differenceInMinutesValue = differenceInMinutes(endDate, startDate);

      setCustomDuration(differenceInMinutesValue);
    }
  }, [startDate, endDate]);

  const calculateAndSetEndDate = () => {
    if (!startDate || !futureChipDuration) return;

    const end = addMinutes(startDate, futureChipDuration);

    setEndDate(end);
  };

  useEffect(() => {
    calculateAndSetEndDate();
  }, [startDate, futureChipDuration]);

  // Conditional rendering logic
  const renderContinueButton = () => {
    if (!isNow && !startDate) return false;
    if (isNow && durationIsCustom && customDuration) return true;
    if (isNow && duration) return false;
    if (isNow && !customDuration) return false;
    if (customDuration || endDate) return true;
    if (isNow || !endDate || !startDate) return false;
    if (!isNow && endDate) return true;

    return false;
  };

  const parseAccordionValueNowTime = (durInMins) => {
    if (durInMins === null) return '';

    if (durInMins >= 60) {
      const hours = Math.floor(durInMins / 60);
      const minutes = durInMins % 60;
      const hourText = hours === 1 ? 'hour' : 'hours';
      const minuteText = minutes === 1 ? 'min' : 'mins';

      if (minutes === 0) {
        return `Next ${hours} ${hourText}`;
      }

      return `Next ${hours} ${hourText} ${minutes} ${minuteText}`;
    }

    return `Next ${durInMins} mins`;
  };

  useEffect(() => {
    if (isNow) {
      setAccordionValue(parseAccordionValueNowTime(duration));
    }
  }, [toggleChoice, duration]);

  function parseAccordionValueFutureTime(durInMins) {
    // Convert the duration to hours and minutes
    const durationHours = Math.floor(durInMins / 60);
    const durationStr =
      durationHours > 0 ? `${durationHours} hours` : `${durInMins} minutes`;

    // Helper function to format the date in the desired output
    function formatDateTime(date) {
      return format(date, 'MM/dd/yyyy, h:mm a');
    }

    // Format the start and end dates
    const startFormatted = formatDateTime(startDate);
    const endFormatted = formatDateTime(endDate);

    // Construct the final string
    return `From ${startFormatted} to ${endFormatted} (${durationStr})`;
  }

  // TODO: Test commented code, and refactor during testing
  const handleChipSelection = (time) => {
    if (isNow && time) {
      setStartDate(now);
      setEndDate(add(now, { minutes: time }));
      setDuration(time);
      setFutureChipDuration(null);
      setDurationIsCustom(false);
    } else if (!time) {
      setDuration(null);
      setFutureChipDuration(null);
      setDurationIsCustom(true);
    } else if (!isNow) {
      if (startDate) {
        setDuration(null);
        setFutureChipDuration(time);
        setDurationIsCustom(false);
      } else {
        setDuration(null);
        setFutureChipDuration(time);
        setDurationIsCustom(false);
      }
    } else {
      setDuration(time);
      setFutureChipDuration(null);
      setDurationIsCustom(false);
    }
  };
  // ! Nathan's suggestion
  // const handleChipSelection = (time) => {
  //   if (!time) {
  //     setDuration(null);
  //     setFutureChipDuration(null);
  //     setDurationIsCustom(true);
  //   } else if (isNow) {
  //     setStartDate(now);
  //     setEndDate(add(now, { minutes: time }));
  //     setDuration(time);
  //     setFutureChipDuration(null);
  //     setDurationIsCustom(false);
  //   } else {
  //     setStartDate(null);
  //     setEndDate(null);
  //     setDuration(null);
  //     setFutureChipDuration(time);
  //     setDurationIsCustom(false);
  //   }
  // };

  // Chips
  const chipSelected = (value) =>
    futureChipDuration === value || duration === value;

  const chipOptions = [
    { value: 30, label: '30 min' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 240, label: '4 hours' },
    { value: 480, label: '8 hours' },
  ];

  const getDurationChips = () => (
    <div className='TimePicker-durationChips-container'>
      <Typography kind='label' className='TimePicker-durationLabel'>
        Duration
      </Typography>
      <div className='TimePicker-durationChips'>
        {chipOptions.map(({ value, label }) => {
          const chipKind = chipSelected(value) ? 'boldFilled' : 'outlined';
          const isSelected = chipSelected(value);
          const isDisabled =
            startDate && add(startDate, { minutes: value }) > rangeMaxDate;

          return (
            <Chip
              key={value}
              kind={chipKind}
              label={label}
              isSelected={isSelected}
              onClick={() => handleChipSelection(value)}
              color='brandSecondary'
              isDisabled={isDisabled}
            />
          );
        })}
        <div className='TimePicker-dividerLine' />
        <Chip
          kind={durationIsCustom ? 'boldFilled' : 'outlined'}
          label='Custom'
          isSelected={durationIsCustom}
          onClick={() => handleChipSelection(false)}
          color='brandSecondary'
        />
      </div>
    </div>
  );

  const getFutureDateAndTime = (type) => {
    if (isNow) return null;

    if (type === 'start') {
      return (
        <div className='TimePicker-dateInputs'>
          {/* Start Date */}
          <DateAndTimePicker
            kind='dateStart'
            label='Start Date'
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            maxDate={rangeMaxDate}
            minDate={rangeMinDate}
          />
          {/* Start Time */}
          <DateAndTimePicker
            kind='timeStart'
            label='Start Time'
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            maxDate={rangeMaxDate}
            minDate={rangeMinDate}
            timeIntervals={15}
            dateFormat='h:mm aa'
            timeHeaderText=''
            timeMin={{ minutes: 0, hours: 0 }}
            timeMax={{ minutes: 0, hours: 23 }}
          />
        </div>
      );
    }

    if (type === 'end' && durationIsCustom) {
      return (
        <div className='TimePicker-dateInputs'>
          {/* End Date */}
          <DateAndTimePicker
            kind='dateEnd'
            label='End Date'
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            maxDate={rangeMaxDate}
            minDate={rangeMinDate}
          />
          {/* Start Time */}
          <DateAndTimePicker
            kind='timeEnd'
            label='End Time'
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            maxDate={rangeMaxDate}
            minDate={rangeMinDate}
            timeIntervals={15}
            dateFormat='h:mm aa'
            timeHeaderText=''
            timeMin={{ minutes: 0, hours: 0 }}
            timeMax={{ minutes: 0, hours: 23 }}
          />
        </div>
      );
    }

    return null;
  };

  const renderCustomDuration = () => {
    if (isNow && durationIsCustom)
      return (
        <div className='TimePicker-customDuration'>
          <div className='TimePicker-inputFields'>
            <div className='TimePicker-enterNumber-input'>
              <InputField
                type='number'
                name='Interval Number'
                placeholder='Enter number'
                label='Custom duration'
                backgroundColor='secondary'
                value={customDuration}
                setValue={setCustomDuration}
                pattern='\d*'
              />
            </div>
            <div className='TimePicker-enterNumber-input'>
              <InputField
                name='Interval Dropdown'
                placeholder='Interval'
                backgroundColor='secondary'
                suffixButtonType='dropdown'
                suffixIsToggle={dropdown}
                suffixOnClick={() => setDropdown(!dropdown)}
                value='Minutes'
                isReadOnly
              />
            </div>
          </div>
        </div>
      );

    if (!isNow && endDate && !durationIsCustom && startDate)
      return (
        <div className='TimePicker-customDuration'>
          <div className='TimePicker-inputFields'>
            <div className='TimePicker-enterNumber-input'>
              <DateAndTimePicker
                kind='dateEnd'
                label='End Date'
                isReadOnly={!durationIsCustom}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                maxDate={rangeMaxDate}
                minDate={rangeMinDate}
              />
            </div>
            <div className='TimePicker-enterNumber-input'>
              <DateAndTimePicker
                kind='timeEnd'
                label='End Time'
                isReadOnly={!durationIsCustom}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                maxDate={rangeMaxDate}
                minDate={rangeMinDate}
                timeIntervals={60}
                dateFormat='h:mm aa'
                timeHeaderText=''
                timeMin={{ minutes: 0, hours: 0 }}
                timeMax={{ minutes: 0, hours: 23 }}
              />
            </div>
          </div>
        </div>
      );

    return null;
  };

  const handleContinueButton = () => {
    if (isNow) {
      setDuration(customDuration);
      setStartDate(new Date());
      setEndDate(add(now, { minutes: customDuration }));
      setAccordionValue(parseAccordionValueNowTime(customDuration));
    } else {
      setDuration(customDuration);
      setAccordionValue(
        parseAccordionValueFutureTime(futureChipDuration || customDuration)
      );
    }
  };

  return (
    <div className='TimePicker'>
      <ToggleButtonGroup
        selection={toggleChoice}
        setSelection={setToggleChoice}>
        <ToggleButton label='Now' />
        <ToggleButton label='Future' />
      </ToggleButtonGroup>
      {getFutureDateAndTime('start')}
      {getDurationChips()}
      {renderCustomDuration()}
      {getFutureDateAndTime('end')}
      {renderContinueButton() && (
        <div className='TimePicker-continueButton'>
          <Button
            type='button'
            kind='filled'
            onClick={() => handleContinueButton()}>
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}

TimePicker.propTypes = {
  duration: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  setAccordionValue: PropTypes.func,
  setDuration: PropTypes.func,
  setStartDate: PropTypes.func,
  setEndDate: PropTypes.func,
};

TimePicker.defaultProps = {
  duration: null,
  startDate: null,
  endDate: null,
  setAccordionValue: () => {},
  setDuration: () => {},
  setStartDate: () => {},
  setEndDate: () => {},
};

export default React.memo(TimePicker);
